/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.19.0
 * @since 1.0.0
 */
import { PureComponent } from 'react';

import {
    IPageExpensesAnalyzerTableTranslation,
    IPageSubscriberMobileTableTranslation,
} from '../../modules/internationalization/types';
import { IPageBillsTable } from '../../modules/internationalization/types/pages/bills';
import { IPagePersonalAccountTable } from '../../modules/internationalization/types/pages/personalAccount';
import { Button, Checkbox, LinkBlock, TextBlock } from '../common';
import { ITableField } from '../Table/reducers/tableReducer';
import DragAndDrop from './components/DragAndDrop/DragAndDrop';
import {
    BodyStyled,
    DragAndDropContainerStyled,
    DragAndDropItemStyled,
    FooterCloseStyled,
    FooterContainerStyled,
    FootnoteStyled,
    HeaderStyled,
    SettingContainerStyled,
    SubTitleStyled,
    TitleStyled,
} from './styled/TableSettingStyled';

export interface ITableSettingProps<Field> {
    fields: Array<ITableField<Field>>;
    isOpen: boolean;
    locale: ITableSettingLocale;
    onApply: (tableFields: Array<ITableField<Field>>) => void;
    onClose: () => void;
    requiredFields: Array<ITableField<Field>>;
}

interface ITableSettingLocale {
    table: ITableSettingTableLocale;
    title: ITableSettingTitleLocale;
}

interface ITableSettingTitleLocale {
    applyBtn: string;
    closeBtn: string;
    footnote?: string;
    subTitle: string;
    title: string;
}

type ITableSettingTableLocale =
    IPageBillsTable
    | IPagePersonalAccountTable
    | IPageExpensesAnalyzerTableTranslation
    | IPageSubscriberMobileTableTranslation
    ;

interface ITableSettingState<Field> {
    fields: Array<ITableField<Field>>;
}

/**
 * TODO: move to modules/table
 */
class TableSetting<Field> extends PureComponent<ITableSettingProps<Field>, ITableSettingState<Field>> {

    state = {
        fields: this.props.fields,
    };

    onCheck = (index: number) => {
        const fields = [...this.state.fields];
        fields[index] = { ...fields[index], checked: !fields[index].checked };
        this.setState({
            fields: [...fields],
        });
    };
    onApply = (): void => {
        this.props.onApply([...this.props.requiredFields, ...this.state.fields]);
    };

    render() {
        const { locale } = this.props;
        return (
            <SettingContainerStyled
                data-name={'table-settings'}>
                <HeaderStyled data-name='modal-popup-header'>
                    <TitleStyled data-name='modal-popup-title'>
                        {locale.title.title}
                    </TitleStyled>
                    <SubTitleStyled>
                        {locale.title.subTitle}
                    </SubTitleStyled>
                </HeaderStyled>
                <BodyStyled data-name='modal-popup-content'>
                    <DragAndDropContainerStyled>
                        {
                            this.renderRequiredFields()
                        }
                        <DragAndDrop
                            items={this.renderFields()}
                            onChange={this.onDragAndDropChange}
                        />
                    </DragAndDropContainerStyled>
                </BodyStyled>
                <FootnoteStyled>
                    {'- ' + locale.title.footnote}
                </FootnoteStyled>
                <FooterContainerStyled data-name='modal-popup-footer'>
                    <Button
                        dataName="applyTableSettings"
                        kind="mediumNormal"
                        onClick={this.onApply}
                    >
                        {locale.title.applyBtn}
                    </Button>
                    <FooterCloseStyled onClick={this.props.onClose}>
                        <LinkBlock dataName="closeTableSettings">
                            {locale.title.closeBtn}
                        </LinkBlock>
                    </FooterCloseStyled>
                </FooterContainerStyled>
            </SettingContainerStyled>
        );
    }

    /**
     * @description Handler onChange items historyOrder in container.
     * @param {JSX.Element[]} items
     * @return {void}
     */
    private onDragAndDropChange = (items: JSX.Element[]) => {
        const fields: Array<ITableField<Field>> = items.map((value: JSX.Element, i: number) => {
            const index = value.key ? +value.key - this.props.requiredFields.length : 0;
            return {
                ...this.state.fields[index],
                order: i + this.props.requiredFields.length,
            };
        });
        this.setState({
            fields,
        });
    };

    /**
     * @description Render draggable fields for drag ang drop.
     * @return {JSX.Element[]}
     */
    private renderFields = (): JSX.Element[] => {
        return this.state.fields.map((value: ITableField<Field>, index: number) => {

            const columnName = (this.props.locale.table as any)[value.name]?.header ?? value.label;
            const columnDataName = (this.props.locale.table as any)[value.name]?.dataName ?? '';
            return (
                <DragAndDropItemStyled
                    data-drag-and-drop-item={columnDataName}
                    key={value.order}
                    order={value.order}
                    required={value.type === 'M'}
                >
                    {value.type === 'S' && <Checkbox checked={value.checked} onChange={() => this.onCheck(index)}/>}
                    <TextBlock>{columnName}</TextBlock>
                </DragAndDropItemStyled>
            );
        });
    };

    /**
     * @description Render required not draggable items.
     * @return {JSX.Element[]}
     */
    private renderRequiredFields = (): JSX.Element[] => {
        return this.props.requiredFields.map((value: ITableField<Field>) => {
            const columnName = (this.props.locale.table as any)[value.name]?.header ?? value.label;
            return <DragAndDropItemStyled
                className='table-setting-wrapper__setting-body-drag-and-drop-item_no-drop'
                key={value.order}
                order={value.order}
                required={true}
            >
                <TextBlock>{columnName}</TextBlock>
            </DragAndDropItemStyled>;
        });
    };
}

export default TableSetting;
